import React, { useEffect, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, Percent, Users as UsersIcon, ChevronDown, ChevronUp, TrendingUp, Award, Download, Printer } from "lucide-react";
import { printReport } from "@/lib/printReport";
import { usePrintTemplate } from "@/hooks/usePrintTemplate";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { currentMonth, monthEnd, monthOptions, monthStart } from "@/lib/payPeriod";
import { fmt } from "@/lib/format";

export default function Commissions() {
  const { lang, t } = useLanguage();
  const template = usePrintTemplate();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [rules, setRules] = useState([]);
  const [report, setReport] = useState({ rows: [], totals: { total_sales: 0, total_commission: 0, total_items: 0, staff_count: 0 } });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_user_id: "", default_percent: 10, service_overrides: {}, active: true });
  const [from, setFrom] = useState(monthStart(currentMonth()));
  const [to, setTo] = useState(monthEnd(currentMonth()));
  const [periodMonth, setPeriodMonth] = useState(currentMonth());
  const [customRange, setCustomRange] = useState(false);
  const [source, setSource] = useState("all"); // all | invoices | bookings
  const [employeeFilter, setEmployeeFilter] = useState(""); // Wave C — employee dropdown filter
  const [employeeSearch, setEmployeeSearch] = useState(""); // Completion pass — text search across employees
  const [expanded, setExpanded] = useState({}); // employee_user_id → bool
  const [pageTab, setPageTab] = useState("commission"); // commission | tips
  const [tipsReport, setTipsReport] = useState({ rows: [], totals: {} });
  const [staffPaySettings, setStaffPaySettings] = useState(null);
  const [payoutPreview, setPayoutPreview] = useState({ rows: [], totals: {} });

  const load = async () => {
    const [u, s, r] = await Promise.all([api.get("/users"), api.get("/services"), api.get("/commission-rules")]);
    setUsers(u.data); setServices(s.data); setRules(r.data);
  };
  const loadReport = async () => {
    const params = new URLSearchParams({ from_date: from, to_date: to, source });
    if (employeeFilter) params.set("employee_user_id", employeeFilter);
    const r = await api.get(`/commission-report?${params.toString()}`);
    setReport(r.data);
  };
  const loadTipsReport = async () => {
    const params = new URLSearchParams({ from_date: from, to_date: to });
    if (employeeFilter) params.set("employee_user_id", employeeFilter);
    const r = await api.get(`/tips-report?${params.toString()}`);
    setTipsReport(r.data);
  };
  const loadPayoutPreview = async () => {
    try {
      const r = await api.get(`/staff-payouts/preview?period_start=${from}&period_end=${to}`);
      setPayoutPreview(r.data || { rows: [], totals: {} });
    } catch {
      setPayoutPreview({ rows: [], totals: {} });
    }
  };
  const loadStaffPaySettings = async () => {
    const r = await api.get("/settings/staff-pay");
    setStaffPaySettings(r.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadReport(); loadTipsReport(); loadPayoutPreview(); loadStaffPaySettings(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadReport(); loadTipsReport(); loadPayoutPreview(); }, [source, employeeFilter, from, to]);

  const applyMonth = (ym) => {
    setPeriodMonth(ym);
    setFrom(monthStart(ym));
    setTo(monthEnd(ym));
    setCustomRange(false);
  };

  // Wave C — CSV export (server-side at /commission-report/export)
  const exportCsv = async () => {
    try {
      const params = new URLSearchParams({ from_date: from, to_date: to, source });
      if (employeeFilter) params.set("employee_user_id", employeeFilter);
      const url = `${API_BASE}/commission-report/export?${params.toString()}`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error("Export failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `commissions_${from}_${to}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(lang === "ar" ? "تم التصدير" : "Exported");
    } catch (e) { toast.error(lang === "ar" ? "فشل التصدير" : "Export failed"); }
  };

  // Wave C — Print (replaced with professional report builder in Completion Pass v2)
  const printReportPro = (singleEmpId = null) => {
    const rowsAll = singleEmpId ? visibleRows.filter(r => r.employee_user_id === singleEmpId) : visibleRows;
    const subtotalSales = rowsAll.reduce((s, r) => s + (r.total_sales || 0), 0);
    const subtotalCommission = rowsAll.reduce((s, r) => s + (r.commission || 0), 0);
    const subtotalItems = rowsAll.reduce((s, r) => s + (r.items || 0), 0);
    printReport({
      title: lang === "ar" ? "تقرير العمولات" : "Commissions Report",
      subtitle: singleEmpId ? (rowsAll[0]?.employee_name || "") : "",
      lang,
      template,
      meta: [
        { label: lang === "ar" ? "الفترة" : "Period", value: `${from} → ${to}` },
        { label: lang === "ar" ? "المصدر" : "Source", value: source === "all" ? (lang === "ar" ? "الكل" : "All") : source },
        { label: lang === "ar" ? "عدد الموظفين" : "Staff", value: String(rowsAll.length) },
      ],
      columns: [
        lang === "ar" ? "الموظف" : "Employee",
        lang === "ar" ? "المبيعات" : "Sales",
        lang === "ar" ? "العمولة" : "Commission",
        lang === "ar" ? "بنود فواتير" : "Invoice items",
        lang === "ar" ? "بنود حجوزات" : "Booking items",
        lang === "ar" ? "إجمالي البنود" : "Total items",
      ],
      rows: rowsAll.map(r => [
        r.employee_name || "—",
        { value: (r.total_sales || 0).toFixed(2), className: "num" },
        { value: (r.commission || 0).toFixed(2), className: "num pos" },
        { value: String(r.invoice_items || 0), className: "num muted" },
        { value: String(r.booking_items || 0), className: "num muted" },
        { value: String(r.items || 0), className: "num" },
      ]),
      totals: [
        { label: lang === "ar" ? "إجمالي المبيعات" : "Total sales", value: subtotalSales.toFixed(2) },
        { label: lang === "ar" ? "إجمالي العمولات" : "Total commission", value: subtotalCommission.toFixed(2), className: "gold" },
        { label: lang === "ar" ? "إجمالي البنود" : "Total items", value: String(subtotalItems) },
      ],
    });
  };
  const printReportAll = () => printReportPro(null);
  const printEmployee = (emp_id) => printReportPro(emp_id);

  // Filtered visible rows after employee dropdown + text search
  const visibleRows = report.rows.filter(r => {
    if (employeeFilter && r.employee_user_id !== employeeFilter) return false;
    if (employeeSearch) {
      const q = employeeSearch.toLowerCase();
      if (!String(r.employee_name || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const save = async () => {
    if (!form.employee_user_id) return toast.error(lang === "ar" ? "اختر موظف" : "Select employee");
    try {
      await api.post("/commission-rules", form);
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
      setOpen(false); setForm({ employee_user_id: "", default_percent: 10, service_overrides: {}, active: true });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: lang === "ar" ? "حذف القاعدة؟" : "Delete rule?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: lang === "ar" ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/commission-rules/${id}`); load();
  };

  const editRule = (r) => {
    setForm({
      employee_user_id: r.employee_user_id,
      default_percent: r.default_percent,
      service_overrides: r.service_overrides || {},
      active: r.active !== false,
    });
    setOpen(true);
  };

  const saveStaffPaySettings = async (patch) => {
    const next = { ...staffPaySettings, ...patch };
    const r = await api.put("/settings/staff-pay", next);
    setStaffPaySettings(r.data);
    toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
  };

  const setOverride = (sid, val) => {
    const overrides = { ...form.service_overrides };
    if (val === "" || val === null) delete overrides[sid];
    else overrides[sid] = parseFloat(val);
    setForm({ ...form, service_overrides: overrides });
  };

  const userMap = users.reduce((m, u) => { m[u.user_id] = u.name || u.username; return m; }, {});

  return (
    <div className="space-y-6" data-testid="commissions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading gold-text">{lang === "ar" ? "عمولات الموظفين" : "Staff Commissions"}</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1 max-w-2xl">{t("commissions_page_help")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/staff-payouts" className="px-3 py-2 rounded-md bg-[var(--gold)] text-white text-sm font-medium">{t("commissions_go_pay")}</Link>
          <button data-testid="add-commission-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-md border border-[var(--gold)] text-[var(--gold)]"><Plus size={16} /> {lang === "ar" ? "قاعدة جديدة" : "New Rule"}</button>
        </div>
      </div>

      {/* Owed summary — same period as Staff Payouts */}
      <div className="bg-[var(--surface)] rounded-xl border border-amber-500/30 p-4" data-testid="commissions-owed-summary">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold">{t("commissions_owed_title")}</div>
            <div className="text-xs text-[var(--text-muted)] mt-0.5">{from} → {to}</div>
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase text-[var(--text-muted)]">{t("commission_owed")}</div>
              <div className="font-bold num-display text-amber-700">{fmt(payoutPreview.totals?.commission)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-[var(--text-muted)]">{t("tips_owed")}</div>
              <div className="font-bold num-display text-amber-700">{fmt(payoutPreview.totals?.tips)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-[var(--text-muted)]">{lang === "ar" ? "الصافي المستحق" : "Total owed"}</div>
              <div className="font-bold num-display gold-text">{fmt(payoutPreview.totals?.net)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-[var(--text-muted)]">{lang === "ar" ? "موظفون" : "Staff"}</div>
              <div className="font-bold num-display">{payoutPreview.totals?.staff_count || 0}</div>
            </div>
          </div>
          <Link to="/staff-payouts" className="text-sm text-[var(--gold)] hover:underline shrink-0">{t("commissions_go_pay")} →</Link>
        </div>
        <p className="text-xs text-[var(--text-muted)] mt-3 border-t border-[var(--border-subtle)] pt-3">{t("commissions_owed_help")}</p>
      </div>

      {staffPaySettings && (
        <div className="bg-[var(--surface)] rounded-xl border border-[var(--border)] p-4 flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!staffPaySettings.tips_enabled} onChange={(e) => saveStaffPaySettings({ tips_enabled: e.target.checked })} />
            {lang === "ar" ? "تفعيل الإكراميات في نقطة البيع" : "Tips enabled at POS"}
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!staffPaySettings.require_staff_on_services} onChange={(e) => saveStaffPaySettings({ require_staff_on_services: e.target.checked })} />
            {lang === "ar" ? "إلزام اختيار موظف لكل خدمة" : "Require staff on services"}
          </label>
        </div>
      )}

      {/* Rules */}
      <div className="bg-[var(--surface)] rounded-xl border border-[var(--border)] p-4">
        <h2 className="font-heading text-lg mb-3">{lang === "ar" ? "قواعد العمولة" : "Commission Rules"}</h2>
        <div className="space-y-2">
          {rules.map((r) => (
            <div key={r.rule_id} className="flex items-center justify-between p-3 bg-[var(--bg)] rounded-md">
              <div className="flex items-center gap-3">
                <UsersIcon size={16} className="text-[var(--gold)]" />
                <div>
                  <div className="font-medium">{userMap[r.employee_user_id] || r.employee_user_id}</div>
                  <div className="text-xs text-[var(--text-muted)]">
                    {lang === "ar" ? "العمولة الافتراضية:" : "Default:"} {r.default_percent}% · {Object.keys(r.service_overrides || {}).length} {lang === "ar" ? "تخصيصات" : "overrides"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => editRule(r)} className="p-1.5 rounded text-[var(--gold)] text-xs">{lang === "ar" ? "تعديل" : "Edit"}</button>
                <button onClick={() => remove(r.rule_id)} className="p-1.5 rounded bg-rose-50 text-rose-500"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {rules.length === 0 && <div className="text-[var(--text-muted)] text-center py-6">{lang === "ar" ? "لا توجد قواعد" : "No rules"}</div>}
        </div>
      </div>

      {/* Report */}
      <div className="bg-[var(--surface)] rounded-xl border border-[var(--border)] p-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <h2 className="font-heading text-lg">{lang === "ar" ? "تقرير العمولات" : "Commission Report"}</h2>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">{t("commissions_report_help")}</p>
          </div>
          <div className="flex gap-2 items-center text-sm flex-wrap">
            <label className="flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
              <input
                type="checkbox"
                checked={customRange}
                onChange={(e) => {
                  const on = e.target.checked;
                  setCustomRange(on);
                  if (!on) applyMonth(periodMonth);
                }}
              />
              {lang === "ar" ? "فترة مخصصة" : "Custom range"}
            </label>
            {customRange ? (
              <>
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="px-3 py-1.5 border rounded-md bg-[var(--bg)]" data-testid="commission-from" />
                <span>—</span>
                <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="px-3 py-1.5 border rounded-md bg-[var(--bg)]" data-testid="commission-to" />
              </>
            ) : (
              <select value={periodMonth} onChange={(e) => applyMonth(e.target.value)} className="px-3 py-1.5 border rounded-md bg-[var(--bg)] num-display" data-testid="commission-month">
                {monthOptions().map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            )}
            <div className="inline-flex rounded-md border border-[var(--border)] bg-[var(--bg)] p-0.5" data-testid="commission-source-toggle">
              {[
                { v: "all", l_ar: "الكل", l_en: "All" },
                { v: "invoices", l_ar: "الفواتير", l_en: "Invoices" },
                { v: "bookings", l_ar: "الحجوزات", l_en: "Bookings" },
              ].map(o => (
                <button key={o.v} onClick={() => setSource(o.v)} data-testid={`source-${o.v}`} className={`px-3 py-1 rounded text-xs ${source === o.v ? "bg-[var(--gold)] text-white" : "text-[var(--text-muted)]"}`}>{lang === "ar" ? o.l_ar : o.l_en}</button>
              ))}
            </div>
            <select value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)} data-testid="commission-employee-filter"
              className="px-3 py-1.5 border rounded-md bg-[var(--bg)] text-sm">
              <option value="">{lang === "ar" ? "كل الموظفين" : "All employees"}</option>
              {users.filter(u => u.role !== "admin").map(u => <option key={u.user_id} value={u.user_id}>{u.name || u.username}</option>)}
            </select>
            <input value={employeeSearch} onChange={e => setEmployeeSearch(e.target.value)} data-testid="commission-employee-search"
              placeholder={lang === "ar" ? "بحث عن موظف" : "Search employee"}
              className="px-3 py-1.5 border rounded-md bg-[var(--bg)] text-sm w-40"/>
            <button onClick={() => { loadReport(); loadTipsReport(); loadPayoutPreview(); }} className="px-3 py-1.5 bg-[var(--gold)] text-white rounded-md" data-testid="commission-refresh">{lang === "ar" ? "تحديث" : "Refresh"}</button>
            <button onClick={exportCsv} data-testid="commission-export-csv"
              className="px-3 py-1.5 border border-[var(--border)] rounded-md flex items-center gap-1 bg-[var(--surface)] hover:border-[var(--gold)]">
              <Download size={13}/>CSV
            </button>
            <button onClick={printReportAll} data-testid="commission-print"
              className="px-3 py-1.5 border border-[var(--border)] rounded-md flex items-center gap-1 bg-[var(--surface)] hover:border-[var(--gold)]">
              <Printer size={13}/>{lang === "ar" ? "طباعة" : "Print"}
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-4">
          <button type="button" onClick={() => setPageTab("commission")} className={`px-3 py-1.5 rounded text-sm ${pageTab === "commission" ? "bg-[var(--gold)] text-white" : "border"}`}>{lang === "ar" ? "العمولات" : "Commissions"}</button>
          <button type="button" onClick={() => setPageTab("tips")} className={`px-3 py-1.5 rounded text-sm ${pageTab === "tips" ? "bg-[var(--gold)] text-white" : "border"}`}>{lang === "ar" ? "الإكراميات" : "Tips"}</button>
        </div>

        {pageTab === "commission" && (
        <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-[var(--bg)] rounded-md p-3 border border-[var(--border-subtle)]">
            <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)] flex items-center gap-1"><UsersIcon size={11}/>{lang === "ar" ? "عدد الموظفين" : "Staff"}</div>
            <div className="text-xl font-bold text-[var(--text)] mt-1">{report.totals?.staff_count || 0}</div>
          </div>
          <div className="bg-[var(--bg)] rounded-md p-3 border border-[var(--border-subtle)]">
            <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)] flex items-center gap-1"><TrendingUp size={11}/>{lang === "ar" ? "إجمالي المبيعات" : "Total Sales"}</div>
            <div className="text-xl font-bold text-[var(--text)] mt-1">{(report.totals?.total_sales || 0).toFixed(2)} <span className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "ر.ق" : "QAR"}</span></div>
          </div>
          <div className="bg-[var(--gold-tint)] rounded-md p-3 border border-[var(--gold)]">
            <div className="text-[10px] uppercase tracking-wide text-[var(--gold)] flex items-center gap-1"><Award size={11}/>{lang === "ar" ? "إجمالي العمولات" : "Total Commission"}</div>
            <div className="text-xl font-bold gold-text mt-1" data-testid="total-commission">{(report.totals?.total_commission || 0).toFixed(2)} <span className="text-xs">{lang === "ar" ? "ر.ق" : "QAR"}</span></div>
          </div>
          <div className="bg-[var(--bg)] rounded-md p-3 border border-[var(--border-subtle)]">
            <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">{lang === "ar" ? "العناصر" : "Items"}</div>
            <div className="text-xl font-bold text-[var(--text)] mt-1">{report.totals?.total_items || 0}</div>
          </div>
        </div>

        <table className="w-full text-sm">
          <thead className="bg-[var(--bg)]">
            <tr className="text-start">
              <th className="p-2 w-8"></th>
              <th className="p-2 text-start">{lang === "ar" ? "الموظف" : "Employee"}</th>
              <th className="p-2 text-end">{lang === "ar" ? "المبيعات" : "Sales"}</th>
              <th className="p-2 text-end gold-text">{lang === "ar" ? "محسوب" : "Calculated"}</th>
              <th className="p-2 text-end">{t("commission_paid")}</th>
              <th className="p-2 text-end">{t("commission_owed")}</th>
              <th className="p-2 text-end">{lang === "ar" ? "فواتير" : "Inv"}</th>
              <th className="p-2 text-end">{lang === "ar" ? "حجوزات" : "Bk"}</th>
              <th className="p-2 text-end">{lang === "ar" ? "إجمالي" : "Items"}</th>
              <th className="p-2 text-end no-print"></th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r) => (
              <React.Fragment key={r.employee_user_id}>
                <tr data-employee-row={r.employee_user_id} className="border-t border-[var(--border)] hover:bg-[var(--bg)] cursor-pointer" onClick={() => setExpanded(p => ({ ...p, [r.employee_user_id]: !p[r.employee_user_id] }))} data-testid={`commission-row-${r.employee_user_id}`}>
                  <td className="p-2">
                    {(r.services || []).length > 0 ? (expanded[r.employee_user_id] ? <ChevronUp size={14}/> : <ChevronDown size={14}/>) : null}
                  </td>
                  <td className="p-2 font-medium">
                    {r.employee_name}
                    {r.accrual_only && (
                      <span className="ms-1 text-[10px] text-[var(--text-muted)]">({lang === "ar" ? "مستحقات فقط" : "accruals only"})</span>
                    )}
                  </td>
                  <td className="p-2 text-end">{r.total_sales.toFixed(2)}</td>
                  <td className="p-2 text-end font-semibold gold-text">{r.commission.toFixed(2)}</td>
                  <td className="p-2 text-end text-emerald-600">{(r.paid_commission || 0).toFixed(2)}</td>
                  <td className="p-2 text-end text-amber-600">{(r.pending_commission || 0).toFixed(2)}</td>
                  <td className="p-2 text-end text-[var(--text-muted)]">{r.invoice_items}</td>
                  <td className="p-2 text-end text-[var(--text-muted)]">{r.booking_items}</td>
                  <td className="p-2 text-end">{r.items}</td>
                  <td className="p-2 text-end no-print">
                    <button onClick={(e) => { e.stopPropagation(); printEmployee(r.employee_user_id); }} data-testid={`print-emp-${r.employee_user_id}`}
                      title={lang === "ar" ? "طباعة هذا الموظف" : "Print this employee"}
                      className="p-1 rounded hover:bg-[var(--bg)] text-[var(--text-muted)] hover:text-[var(--gold)]">
                      <Printer size={12}/>
                    </button>
                  </td>
                </tr>
                {expanded[r.employee_user_id] && (r.services || []).length > 0 && (
                  <tr data-employee-row={r.employee_user_id} className="bg-[var(--bg)]/50">
                    <td colSpan={10} className="p-3">
                      <div className="text-[11px] text-[var(--text-muted)] mb-2 ps-2">{lang === "ar" ? "تفصيل الخدمات/المنتجات" : "Service / Item breakdown"}</div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[var(--text-muted)]">
                            <th className="p-1.5 text-start">{lang === "ar" ? "العنصر" : "Item"}</th>
                            <th className="p-1.5 text-end">{lang === "ar" ? "العدد" : "Count"}</th>
                            <th className="p-1.5 text-end">%</th>
                            <th className="p-1.5 text-end">{lang === "ar" ? "المبيعات" : "Sales"}</th>
                            <th className="p-1.5 text-end gold-text">{lang === "ar" ? "العمولة" : "Comm."}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.services.map((sv, i) => (
                            <tr key={i} className="border-t border-[var(--border-subtle)]">
                              <td className="p-1.5">{sv.name}</td>
                              <td className="p-1.5 text-end">{sv.count}</td>
                              <td className="p-1.5 text-end text-[var(--text-muted)]">{sv.percent}%</td>
                              <td className="p-1.5 text-end">{sv.sales.toFixed(2)}</td>
                              <td className="p-1.5 text-end gold-text">{sv.commission.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {visibleRows.length === 0 && <tr><td colSpan={10} className="text-center text-[var(--text-muted)] py-8">{lang === "ar" ? "لا توجد بيانات في هذه الفترة. تأكد من إنشاء قواعد عمولة وتعيين الموظف على الفواتير/الحجوزات." : "No data in this period. Make sure commission rules exist and employee is set on invoices/bookings."}</td></tr>}
          </tbody>
        </table>
        </>
        )}

        {pageTab === "tips" && (
          <>
          <p className="text-xs text-[var(--text-muted)] mb-3">{t("tips_report_help")}</p>
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg)]">
              <tr>
                <th className="p-2 text-start">{lang === "ar" ? "الموظف" : "Employee"}</th>
                <th className="p-2 text-end">{lang === "ar" ? "الإجمالي" : "Total"}</th>
                <th className="p-2 text-end">{t("commission_paid")}</th>
                <th className="p-2 text-end">{t("tips_owed")}</th>
              </tr>
            </thead>
            <tbody>
              {(tipsReport.rows || []).map((r) => (
                <tr key={r.employee_user_id} className="border-t border-[var(--border)]">
                  <td className="p-2">{r.employee_name}</td>
                  <td className="p-2 text-end">{r.total_tips.toFixed(2)}</td>
                  <td className="p-2 text-end text-emerald-600">{r.paid_tips.toFixed(2)}</td>
                  <td className="p-2 text-end text-amber-600">{r.pending_tips.toFixed(2)}</td>
                </tr>
              ))}
              {(tipsReport.rows || []).length === 0 && (
                <tr><td colSpan={4} className="text-center text-[var(--text-muted)] py-8">{t("tips_report_empty")}</td></tr>
              )}
            </tbody>
          </table>
          </>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}>
          <div className="bg-[var(--surface)] rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-auto" onMouseDown={onModalPanelMouseDown}>
            <h3 className="text-xl font-heading mb-4">{lang === "ar" ? "قاعدة عمولة جديدة" : "New Commission Rule"}</h3>
            <select data-testid="commission-employee" className="w-full px-3 py-2 border rounded-md bg-[var(--bg)]" value={form.employee_user_id} onChange={(e) => setForm({ ...form, employee_user_id: e.target.value })}>
              <option value="">{lang === "ar" ? "اختر موظف" : "Select employee"}</option>
              {users.filter((u) => u.role !== "admin").map((u) => <option key={u.user_id} value={u.user_id}>{u.name} ({u.role})</option>)}
            </select>
            <div className="mt-3">
              <label className="text-sm">{lang === "ar" ? "نسبة العمولة الافتراضية %" : "Default %"}</label>
              <input data-testid="commission-default" type="number" className="w-full px-3 py-2 border rounded-md bg-[var(--bg)]" value={form.default_percent} onChange={(e) => setForm({ ...form, default_percent: parseFloat(e.target.value || 0) })} />
            </div>
            <div className="mt-4">
              <h4 className="text-sm font-semibold mb-2">{lang === "ar" ? "تخصيص لكل خدمة (اختياري)" : "Per-service Override (optional)"}</h4>
              <div className="grid md:grid-cols-2 gap-2 max-h-60 overflow-auto">
                {services.map((s) => (
                  <div key={s.service_id} className="flex items-center gap-2 p-2 bg-[var(--bg)] rounded-md">
                    <span className="flex-1 text-sm truncate">{lang === "ar" ? s.name_ar : s.name_en}</span>
                    <input type="number" placeholder={`${form.default_percent}%`} className="w-20 px-2 py-1 border rounded text-sm" value={form.service_overrides[s.service_id] ?? ""} onChange={(e) => setOverride(s.service_id, e.target.value)} />
                    <Percent size={12} className="text-[var(--text-muted)]" />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setOpen(false)} className="flex-1 px-4 py-2 border rounded-md">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
              <button data-testid="save-commission-btn" onClick={save} className="flex-1 px-4 py-2 bg-[var(--gold)] text-white rounded-md">{lang === "ar" ? "حفظ" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
